import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form } from 'formik';
import DatePicker from '@td/react-datepicker';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import logError from 'app/lib/log-error';
import PROVIDER_BASIC_SEARCH_FORM from '../queries/provider-basic-search-form.graphql';
import { updateMessage, providerSearch } from '../styles.module.scss';

const ProviderBasicSearchForm = ({ setSearchParams, isFetching, setIsFetching, showOnShift }) => {
  const { loading, error, data } = useQuery(PROVIDER_BASIC_SEARCH_FORM);

  if (error) {
    logError({ error }, 'Failed to fetch provider search form.');
    return <div className={updateMessage}>Search form unavailable, please refresh the page and try again.</div>;
  }

  if (loading || !data) return <div className={updateMessage}>Loading form...</div>;

  const {
    providerBasicSearchForm: {
      availableStates,
      availablePaymentMethods,
      availableProviderAffiliationGroups,
      availableTpns
  } } = data;

  const DatePickerField = ({ name, value, onChange, minDate, maxDate }) => (
    <DatePicker
      minDate={minDate}
      maxDate={maxDate}
      showTimeSelect
      selected={(value && new Date(value)) || null}
      onChange={val => {
        onChange(name, val);
      }}
      dateFormat="MMMM d, yyyy h:mm aa"
    />
    );

  return (
    <Formik
      initialValues={{
        userNm:   '',
        firstNm:  '',
        lastNm:   '',
        id:       '',
        stateCd:  '',
        inOutFlg: '',

        onShift:        '',
        onShiftStartDt: new Date(),
        onShiftEndDt:   new Date(),

        paymentMethodCd:            '',
        providerAffiliationGroupCd: '',
        tpnCd:                      ''
      }}
      onSubmit={values => {
        setIsFetching(true);
        setSearchParams(values);
      }}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <table className={`fields ${providerSearch}`}>
            <tbody>
              <tr>
                <th>
                  <label htmlFor="userNm">Username</label>
                </th>
                <td>
                  <Field name="userNm" type="text" />
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="firstNm">First Name</label>
                </th>
                <td>
                  <Field name="firstNm" type="text" />
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="lastNm">Last Name</label>
                </th>
                <td>
                  <Field name="lastNm" type="text" />
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="id">Provider ID</label>
                </th>
                <td>
                  <Field name="id" type="text" />
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="stateCd">State Code</label>
                </th>
                <td>
                  <Field name="stateCd" component="select">
                    <option value="" />
                    {availableStates.map(({ code, name }) => <option value={code} key={code}>{name}</option>)}
                  </Field>
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="inOutFlg">Clock In/Out Status</label>
                </th>
                <td>
                  <Field name="inOutFlg" component="select">
                    <option value="" />
                    <option value="Y">In</option>
                    <option value="N">Out</option>
                  </Field>
                </td>
              </tr>

              { showOnShift &&
                <tr>
                  <th>
                    <label htmlFor="onShift">Calendar </label>
                  </th>
                  <td>
                    <Field name="onShift" component="select">
                      <option value="" />
                      <option value="onShift">On Shift</option>
                    </Field>
                  </td>
                </tr>
              }
              {
                (showOnShift && values.onShift === 'onShift') &&
                <React.Fragment>
                  <tr className="on-shift-datepicker">
                    <th />
                    <td>
                      <label htmlFor="onShiftStartDt">Start Date/Time </label>
                      <DatePickerField
                        name="onShiftStartDt"
                        value={values.onShiftStartDt}
                        onChange={
                          (name, val) => {
                            if (moment(values.onShiftEndDt).isBefore(val)) {
                              setFieldValue('onShiftEndDt', val);
                            }
                            return setFieldValue(name, val);
                          }
                        }
                      />
                    </td>
                  </tr>
                  <tr className="on-shift-datepicker">
                    <th />
                    <td>
                      <label htmlFor="onShiftEndDt">End Date/Time </label>
                      <DatePickerField
                        minDate={values.onShiftStartDt}
                        name="onShiftEndDt"
                        value={values.onShiftEndDt}
                        onChange={setFieldValue}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              }
              <tr>
                <th>
                  <label htmlFor="paymentMethodCd">Provider Payment Method</label>
                </th>
                <td>
                  <Field name="paymentMethodCd" component="select">
                    <option value="" />
                    {availablePaymentMethods.map(({ code, name }) => <option value={code} key={code}>{name}</option>)}
                  </Field>
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="providerAffiliationGroupCd">Provider Affiliation Group</label>
                </th>
                <td>
                  <Field name="providerAffiliationGroupCd" component="select">
                    <option value="" />
                    {availableProviderAffiliationGroups.map(({ code, name }) => <option value={code} key={code}>{name}</option>)}
                  </Field>
                </td>
              </tr>
              <tr>
                <th>
                  <label htmlFor="tpnCd">TPN</label>
                </th>
                <td>
                  <Field name="tpnCd" component="select">
                    <option value="" />
                    {availableTpns.map(({ code, name }) => <option value={code} key={code}>{name}</option>)}
                  </Field>
                </td>
              </tr>
            </tbody>
          </table>
          <input
            type="submit"
            value={`${isFetching ? 'searching ...' : 'search'}`}
            disabled={isFetching}
          />
        </Form>
      )}
    </Formik>
  );
};

ProviderBasicSearchForm.propTypes = {
  setSearchParams: PropTypes.func,
  isFetching:      PropTypes.bool,
  setIsFetching:   PropTypes.func,
  showOnShift:     PropTypes.bool
};

export default ProviderBasicSearchForm;
